import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  TextField,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  RadioGroup,
  FormControlLabel,
  Radio,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Monitor as MonitorIcon,
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  LocalShipping as ShippingIcon,
  Warning as WarningIcon,
  Schedule as ScheduleIcon,
  Refresh as RefreshIcon,
  OpenInNew as OpenInNewIcon,
  Delete as DeleteIcon,
  Inventory as InventoryIcon,
} from '@mui/icons-material';
import { format, parseISO, isPast } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  Delivery,
  DeliveryStatus,
  DeliveryItem,
  ReceptionStatus,
  DELIVERY_STATUS_CONFIG,
} from '../types';
import { deliveryService } from '../services/deliveryService';
import { dataService } from '../services/dataService';
import { colors } from '../theme';

// ============================================================================
// ReceptionManager - Vista administrativa de entregas
// ============================================================================

const ReceptionManager: React.FC = () => {
  const [deliveries, setDeliveries] = useState<Delivery[]>([]);
  const [locations, setLocations] = useState<string[]>([]);
  const [suppliers, setSuppliers] = useState<string[]>([]);

  // Filters
  const [filterLocation, setFilterLocation] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterSupplier, setFilterSupplier] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Dialogs
  const [receptionDialogOpen, setReceptionDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedDelivery, setSelectedDelivery] = useState<Delivery | null>(null);
  const [receptionStatus, setReceptionStatus] = useState<ReceptionStatus>('conforme');
  const [receptionObservations, setReceptionObservations] = useState('');
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Create form
  const [newDelivery, setNewDelivery] = useState({
    supplierName: '',
    locationName: '',
    estimatedArrival: '',
    status: 'scheduled' as DeliveryStatus,
    purchaseOrderRef: '',
    notes: '',
    items: [{ description: '', category: '', quantity: 0, unit: 'unidades' }] as Array<{
      description: string;
      category: string;
      quantity: number;
      unit: string;
    }>,
  });

  // Wallboard location selector
  const [wallboardDialogOpen, setWallboardDialogOpen] = useState(false);
  const [wallboardLocation, setWallboardLocation] = useState('');

  const loadData = useCallback(() => {
    const allDeliveries = deliveryService.getAll();
    setDeliveries(allDeliveries);
    setLocations(deliveryService.getLocations());
    setSuppliers(dataService.getSuppliers().slice(0, 50));
  }, []);

  useEffect(() => {
    loadData();
    const handler = () => loadData();
    window.addEventListener('deliveriesUpdated', handler);
    return () => window.removeEventListener('deliveriesUpdated', handler);
  }, [loadData]);

  // =========================================================================
  // Filtered deliveries
  // =========================================================================

  const filteredDeliveries = deliveries.filter(d => {
    if (filterLocation !== 'all' && d.locationName !== filterLocation) return false;
    if (filterStatus !== 'all' && d.status !== filterStatus) return false;
    if (filterSupplier !== 'all' && d.supplierName !== filterSupplier) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      if (
        !d.supplierName.toLowerCase().includes(q) &&
        !d.locationName.toLowerCase().includes(q) &&
        !d.purchaseOrderRef?.toLowerCase().includes(q) &&
        !d.items.some(i => i.description.toLowerCase().includes(q))
      ) {
        return false;
      }
    }
    return true;
  });

  // Sort: delayed first, then by ETA
  const sortedDeliveries = [...filteredDeliveries].sort((a, b) => {
    const priorityA = DELIVERY_STATUS_CONFIG[a.status].priority;
    const priorityB = DELIVERY_STATUS_CONFIG[b.status].priority;
    if (priorityA !== priorityB) return priorityA - priorityB;
    return new Date(a.estimatedArrival).getTime() - new Date(b.estimatedArrival).getTime();
  });

  const paginatedDeliveries = sortedDeliveries.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // =========================================================================
  // Stats
  // =========================================================================

  const stats = deliveryService.getStats();

  // =========================================================================
  // Actions
  // =========================================================================

  const handleConfirmReception = () => {
    if (!selectedDelivery) return;
    deliveryService.confirmReception({
      deliveryId: selectedDelivery.id,
      receivedBy: 'Demo User',
      status: receptionStatus,
      observations: receptionObservations || undefined,
    });
    setReceptionDialogOpen(false);
    setSelectedDelivery(null);
    setReceptionStatus('conforme');
    setReceptionObservations('');
    loadData();
    setSnackbar({
      open: true,
      message: `Recepción confirmada como "${receptionStatus.toUpperCase()}"`,
      severity: 'success',
    });
  };

  const handleCreateDelivery = () => {
    if (!newDelivery.supplierName || !newDelivery.locationName || !newDelivery.estimatedArrival) {
      setSnackbar({ open: true, message: 'Completar proveedor, ubicación y ETA', severity: 'error' });
      return;
    }
    const items: DeliveryItem[] = newDelivery.items
      .filter(i => i.description)
      .map((i) => ({
        id: crypto.randomUUID(),
        description: i.description,
        category: i.category || i.description,
        quantity: i.quantity || 1,
        unit: i.unit || 'unidades',
      }));

    if (items.length === 0) {
      setSnackbar({ open: true, message: 'Agregar al menos un item', severity: 'error' });
      return;
    }

    deliveryService.create({
      supplierId: `supplier-${newDelivery.supplierName.toLowerCase().replace(/\s+/g, '-')}`,
      supplierName: newDelivery.supplierName,
      locationId: `loc-${newDelivery.locationName.toLowerCase().replace(/\s+/g, '-')}`,
      locationName: newDelivery.locationName,
      items,
      status: newDelivery.status,
      estimatedArrival: new Date(newDelivery.estimatedArrival).toISOString(),
      purchaseOrderRef: newDelivery.purchaseOrderRef || undefined,
      notes: newDelivery.notes || undefined,
    });

    setCreateDialogOpen(false);
    setNewDelivery({
      supplierName: '',
      locationName: '',
      estimatedArrival: '',
      status: 'scheduled',
      purchaseOrderRef: '',
      notes: '',
      items: [{ description: '', category: '', quantity: 0, unit: 'unidades' }],
    });
    loadData();
    setSnackbar({ open: true, message: 'Entrega creada exitosamente', severity: 'success' });
  };

  const handleDelete = (id: string) => {
    deliveryService.delete(id);
    loadData();
    setSnackbar({ open: true, message: 'Entrega eliminada', severity: 'info' });
  };

  const handleChangeStatus = (id: string, status: DeliveryStatus) => {
    deliveryService.updateStatus(id, status);
    loadData();
  };

  const handleOpenWallboard = () => {
    if (!wallboardLocation) return;
    const encodedLocation = encodeURIComponent(wallboardLocation);
    window.open(`/wallboard/${encodedLocation}`, '_blank');
    setWallboardDialogOpen(false);
  };

  const handleResetMockData = () => {
    deliveryService.resetMockData();
    loadData();
    setSnackbar({ open: true, message: 'Datos de demo reiniciados', severity: 'info' });
  };

  // =========================================================================
  // Render
  // =========================================================================

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: colors.brand.blue }}>
            Recepción de Mercadería
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
            Gestión de entregas y monitor tipo aeropuerto por ubicación
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={handleResetMockData}
          >
            Reset Demo
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Nueva Entrega
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<MonitorIcon />}
            onClick={() => setWallboardDialogOpen(true)}
          >
            Abrir Wallboard
          </Button>
        </Box>
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Entregas Hoy', value: stats.today, icon: <ScheduleIcon />, color: colors.brand.blue },
          { label: 'En Tránsito', value: stats.inTransit, icon: <ShippingIcon />, color: '#ff9800' },
          { label: 'En Puerto', value: stats.atPort, icon: <CheckCircleIcon />, color: '#4caf50' },
          { label: 'Demoradas', value: stats.delayed, icon: <WarningIcon />, color: '#d32f2f' },
        ].map((kpi) => (
          <Grid item xs={6} md={3} key={kpi.label}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  backgroundColor: `${kpi.color}15`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  {React.cloneElement(kpi.icon, { sx: { color: kpi.color, fontSize: 32 } })}
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: kpi.color }}>
                    {kpi.value}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {kpi.label}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              size="small"
              label="Buscar..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth
              size="small"
              select
              label="Ubicación"
              value={filterLocation}
              onChange={(e) => setFilterLocation(e.target.value)}
            >
              <MenuItem value="all">Todas</MenuItem>
              {locations.map(loc => (
                <MenuItem key={loc} value={loc}>{loc}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth
              size="small"
              select
              label="Estado"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <MenuItem value="all">Todos</MenuItem>
              {(Object.entries(DELIVERY_STATUS_CONFIG) as [DeliveryStatus, typeof DELIVERY_STATUS_CONFIG[DeliveryStatus]][]).map(([key, cfg]) => (
                <MenuItem key={key} value={key}>
                  {cfg.icon} {cfg.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth
              size="small"
              select
              label="Proveedor"
              value={filterSupplier}
              onChange={(e) => setFilterSupplier(e.target.value)}
            >
              <MenuItem value="all">Todos</MenuItem>
              {[...new Set(deliveries.map(d => d.supplierName))].sort().map(s => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {/* Deliveries Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Estado</TableCell>
                <TableCell>Proveedor</TableCell>
                <TableCell>Ubicación</TableCell>
                <TableCell>Mercadería</TableCell>
                <TableCell>ETA</TableCell>
                <TableCell>PO</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedDeliveries.map((delivery) => {
                const cfg = DELIVERY_STATUS_CONFIG[delivery.status];
                return (
                  <TableRow key={delivery.id} hover>
                    <TableCell>
                      <Chip
                        label={cfg.label}
                        size="small"
                        sx={{
                          backgroundColor: cfg.bgColor,
                          color: cfg.color,
                          fontWeight: 600,
                          fontSize: '0.75rem',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {delivery.supplierName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {delivery.locationName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {delivery.items.map((item, i) => (
                        <Typography key={item.id} variant="body2" sx={{ fontSize: '0.8rem' }}>
                          {item.description} ({item.quantity} {item.unit})
                          {i < delivery.items.length - 1 && ', '}
                        </Typography>
                      ))}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{
                        color: isPast(parseISO(delivery.estimatedArrival)) && !['received', 'cancelled'].includes(delivery.status)
                          ? '#d32f2f'
                          : 'inherit',
                        fontWeight: isPast(parseISO(delivery.estimatedArrival)) && !['received', 'cancelled'].includes(delivery.status)
                          ? 600 : 400,
                      }}>
                        {format(parseISO(delivery.estimatedArrival), 'dd/MM HH:mm', { locale: es })}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                        {delivery.purchaseOrderRef || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                        {['at_port', 'receiving'].includes(delivery.status) && (
                          <Tooltip title="Confirmar Recepción">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => {
                                setSelectedDelivery(delivery);
                                setReceptionDialogOpen(true);
                              }}
                            >
                              <CheckCircleIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {delivery.status === 'scheduled' && (
                          <Tooltip title="Marcar En Tránsito">
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => handleChangeStatus(delivery.id, 'in_transit')}
                            >
                              <ShippingIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {delivery.status === 'in_transit' && (
                          <Tooltip title="Marcar En Puerto">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleChangeStatus(delivery.id, 'at_port')}
                            >
                              <InventoryIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Eliminar">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(delivery.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
              {paginatedDeliveries.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No se encontraron entregas con los filtros seleccionados
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={sortedDeliveries.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          labelRowsPerPage="Filas por página"
        />
      </Paper>

      {/* ================================================================= */}
      {/* Reception Confirmation Dialog */}
      {/* ================================================================= */}
      <Dialog
        open={receptionDialogOpen}
        onClose={() => setReceptionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          Confirmar Recepción
        </DialogTitle>
        <DialogContent>
          {selectedDelivery && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Proveedor: <strong>{selectedDelivery.supplierName}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Ubicación: <strong>{selectedDelivery.locationName}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Items:
              </Typography>
              {selectedDelivery.items.map(item => (
                <Typography key={item.id} variant="body2" sx={{ ml: 2, fontSize: '0.85rem' }}>
                  • {item.description} — {item.quantity} {item.unit}
                </Typography>
              ))}

              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Estado de la recepción:
                </Typography>
                <RadioGroup
                  value={receptionStatus}
                  onChange={(e) => setReceptionStatus(e.target.value as ReceptionStatus)}
                >
                  <FormControlLabel
                    value="conforme"
                    control={<Radio color="success" />}
                    label="✅ Conforme — Todo recibido correctamente"
                  />
                  <FormControlLabel
                    value="parcial"
                    control={<Radio color="warning" />}
                    label="⚠️ Parcial — Faltante o diferencias"
                  />
                  <FormControlLabel
                    value="rechazado"
                    control={<Radio color="error" />}
                    label="❌ Rechazado — No aceptado"
                  />
                </RadioGroup>
              </Box>

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Observaciones"
                value={receptionObservations}
                onChange={(e) => setReceptionObservations(e.target.value)}
                sx={{ mt: 2 }}
                placeholder="Detallar faltantes, diferencias, motivo de rechazo..."
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setReceptionDialogOpen(false)}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirmReception}
            startIcon={<CheckCircleIcon />}
          >
            Confirmar Recepción
          </Button>
        </DialogActions>
      </Dialog>

      {/* ================================================================= */}
      {/* Create Delivery Dialog */}
      {/* ================================================================= */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          Nueva Entrega
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Proveedor *"
                value={newDelivery.supplierName}
                onChange={(e) => setNewDelivery({ ...newDelivery, supplierName: e.target.value })}
              >
                {suppliers.map(s => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Ubicación *"
                value={newDelivery.locationName}
                onChange={(e) => setNewDelivery({ ...newDelivery, locationName: e.target.value })}
              >
                {locations.map(loc => (
                  <MenuItem key={loc} value={loc}>{loc}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="datetime-local"
                label="ETA *"
                value={newDelivery.estimatedArrival}
                onChange={(e) => setNewDelivery({ ...newDelivery, estimatedArrival: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Estado Inicial"
                value={newDelivery.status}
                onChange={(e) => setNewDelivery({ ...newDelivery, status: e.target.value as DeliveryStatus })}
              >
                <MenuItem value="scheduled">Programado</MenuItem>
                <MenuItem value="in_transit">En Tránsito</MenuItem>
                <MenuItem value="at_port">En Puerto</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Ref. Orden de Compra"
                value={newDelivery.purchaseOrderRef}
                onChange={(e) => setNewDelivery({ ...newDelivery, purchaseOrderRef: e.target.value })}
                placeholder="PO-PY-XXXX"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Notas"
                value={newDelivery.notes}
                onChange={(e) => setNewDelivery({ ...newDelivery, notes: e.target.value })}
              />
            </Grid>

            {/* Items */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, mt: 1 }}>
                Items de la entrega
              </Typography>
              {newDelivery.items.map((item, idx) => (
                <Grid container spacing={1} key={idx} sx={{ mb: 1 }}>
                  <Grid item xs={5}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Descripción"
                      value={item.description}
                      onChange={(e) => {
                        const items = [...newDelivery.items];
                        items[idx] = { ...items[idx], description: e.target.value };
                        setNewDelivery({ ...newDelivery, items });
                      }}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Cantidad"
                      type="number"
                      value={item.quantity || ''}
                      onChange={(e) => {
                        const items = [...newDelivery.items];
                        items[idx] = { ...items[idx], quantity: Number(e.target.value) };
                        setNewDelivery({ ...newDelivery, items });
                      }}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Unidad"
                      value={item.unit}
                      onChange={(e) => {
                        const items = [...newDelivery.items];
                        items[idx] = { ...items[idx], unit: e.target.value };
                        setNewDelivery({ ...newDelivery, items });
                      }}
                    />
                  </Grid>
                  <Grid item xs={1} sx={{ display: 'flex', alignItems: 'center' }}>
                    {newDelivery.items.length > 1 && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          const items = newDelivery.items.filter((_, i) => i !== idx);
                          setNewDelivery({ ...newDelivery, items });
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Grid>
                </Grid>
              ))}
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => {
                  setNewDelivery({
                    ...newDelivery,
                    items: [...newDelivery.items, { description: '', category: '', quantity: 0, unit: 'unidades' }],
                  });
                }}
              >
                Agregar Item
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCreateDialogOpen(false)}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateDelivery}
            startIcon={<AddIcon />}
          >
            Crear Entrega
          </Button>
        </DialogActions>
      </Dialog>

      {/* ================================================================= */}
      {/* Wallboard Location Selector Dialog */}
      {/* ================================================================= */}
      <Dialog
        open={wallboardDialogOpen}
        onClose={() => setWallboardDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          <MonitorIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Abrir Wallboard
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Seleccionar ubicación/remolcador para mostrar en el monitor:
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Ubicación</InputLabel>
            <Select
              value={wallboardLocation}
              onChange={(e) => setWallboardLocation(e.target.value)}
              label="Ubicación"
            >
              {locations.map(loc => (
                <MenuItem key={loc} value={loc}>
                  {loc}
                  {' '}
                  ({deliveries.filter(d => d.locationName === loc && !['received', 'cancelled'].includes(d.status)).length} pendientes)
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setWallboardDialogOpen(false)}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleOpenWallboard}
            disabled={!wallboardLocation}
            startIcon={<OpenInNewIcon />}
          >
            Abrir en Nueva Pestaña
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReceptionManager;
